import React from "react";
import Layout from "@/components/Layout";
import { SUCCESS_MEASURES, CHECKPOINTS } from "@/data/bridgeData";
import { useEntries } from "@/context/EntriesContext";
import { RadarChart, PolarGrid, PolarAngleAxis, Radar, ResponsiveContainer, Legend } from "recharts";

const CP_COLORS = { week0: "#D2CFC7", week8: "#C48A71", week16: "#8C6B5D" };
const AXIS_TICK = { fontSize: 11, fill: "#78716C" };
const LEGEND_STYLE = { fontSize: 12 };

export default function Measures() {
  const { getEntry, saveEntry } = useEntries();
  const scales = SUCCESS_MEASURES.filter((m) => m.type === "scale");
  const counts = SUCCESS_MEASURES.filter((m) => m.type === "count");

  const radarData = scales.map((m) => {
    const row = { metric: m.label.replace(/^Clarity of |^Confidence /, "") };
    CHECKPOINTS.forEach((cp) => { row[cp.id] = Number(getEntry(`measure:${cp.id}:${m.id}`, 0)) || 0; });
    return row;
  });

  return (
    <Layout coachContext>
      <div className="label-eyebrow mb-3">Success measures</div>
      <h1 className="font-serif text-4xl sm:text-5xl font-light text-stone-900 mb-3">Tracking the shift</h1>
      <p className="max-w-2xl text-stone-600 leading-relaxed mb-10">
        Captured at Week 0, Week 8, and Week 16 – clarity, confidence, and alignment, plus real activity in the market.
      </p>

      {/* Radar */}
      <div className="rounded-sm border border-stone-200 bg-white p-6 mb-10">
        <div className="label-eyebrow mb-4">Confidence & clarity (1–10)</div>
        <div style={{ width: "100%", height: 340 }}>
          <ResponsiveContainer>
            <RadarChart data={radarData} outerRadius="72%">
              <PolarGrid stroke="#E5E1DA" />
              <PolarAngleAxis dataKey="metric" tick={AXIS_TICK} />
              {CHECKPOINTS.map((cp) => (
                <Radar key={cp.id} name={cp.label} dataKey={cp.id} stroke={CP_COLORS[cp.id]} fill={CP_COLORS[cp.id]} fillOpacity={0.25} />
              ))}
              <Legend wrapperStyle={LEGEND_STYLE} />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Sliders per checkpoint */}
      <div className="grid lg:grid-cols-3 gap-6 mb-10">
        {CHECKPOINTS.map((cp) => (
          <div key={cp.id} className="rounded-sm border border-stone-200 bg-white p-6">
            <div className="flex items-center gap-2 mb-5">
              <span className="h-3 w-3 rounded-full" style={{ background: CP_COLORS[cp.id] }} />
              <h3 className="font-serif text-2xl text-stone-900">{cp.label}</h3>
            </div>
            <div className="space-y-5">
              {scales.map((m) => {
                const key = `measure:${cp.id}:${m.id}`;
                const val = Number(getEntry(key, 0)) || 0;
                return (
                  <div key={m.id}>
                    <div className="flex justify-between items-baseline mb-1.5">
                      <span className="text-sm text-stone-600 leading-tight pr-2">{m.label}</span>
                      <span className="font-serif text-xl text-brand-primary">{val}</span>
                    </div>
                    <input
                      data-testid={`measure-${cp.id}-${m.id}`}
                      type="range" min="0" max="10" value={val}
                      onChange={(e) => saveEntry(key, Number(e.target.value), true)}
                      className="bridge-range"
                    />
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Counts */}
      <div className="rounded-sm border border-stone-200 bg-brand-parchment/40 p-7">
        <div className="label-eyebrow mb-5">Market activity</div>
        <div className="grid sm:grid-cols-2 gap-6">
          {counts.map((m) => {
            const key = `measure:total:${m.id}`;
            const val = getEntry(key, 0);
            return (
              <div key={m.id} className="flex items-center justify-between rounded-sm border border-stone-200 bg-white p-5">
                <span className="text-stone-700">{m.label}</span>
                <input
                  data-testid={`count-${m.id}`}
                  type="number" min="0" value={val}
                  onChange={(e) => saveEntry(key, Number(e.target.value), true)}
                  className="w-20 bg-transparent border-b border-stone-300 py-1 text-right font-serif text-2xl text-brand-primary focus:outline-none focus:border-brand-primary"
                />
              </div>
            );
          })}
        </div>
      </div>
    </Layout>
  );
}
